import { useEffect, useState } from "react";
import { api } from "../../api/client";
import {
  Badge,
  Button,
  cx,
  DataCard,
  Field,
  Icon,
  Input,
  Modal,
  PageTitle,
  Select,
  Spinner,
  Td,
  Th
} from "../../components/ui";

// Role login/akses (kunci kanonik = users.roles). Management hanya utk super admin.
const ROLE_OPTIONS = [
  { key: "trainer", label: "Trainer" },
  { key: "admin", label: "Admin" },
  { key: "team_leader", label: "Team Leader" },
  // Staff non-trainer yang digaji per sesi (IT, kurir, satpam, dsb).
  //
  // Sebelum role ini ada, satu-satunya cara memasukkan mereka ke Staff Payroll
  // adalah memberi akun ADMIN — yang berarti memberi kuasa mengubah tarif, gaji,
  // dan akun orang lain, hanya supaya bisa dibayar. Role ini memutus keterkaitan
  // itu: bisa dicentang di Staff Payroll, tapi portalnya cuma slip gajinya sendiri.
  { key: "staff", label: "Staff" },
  { key: "management", label: "Management" }
];
const ROLE_LABEL = Object.fromEntries(ROLE_OPTIONS.map((r) => [r.key, r.label]));

// ── Peran per cabang (Trainer / Team Leader) ──────────────────────────────────
// Akun yang Trainer sekaligus Team Leader bisa dibedakan per cabang: mis. trainer di BR01,
// team leader di BR07. Hak team leader hanya berlaku di cabang tempat ia team leader.
// Server mengirim `effectiveBranchRoles` (untuk tampilan); pilihan dikirim sebagai `branchRoles`.
const PERAN_CABANG = ["trainer", "team_leader"];
const SINGKAT = { trainer: "Trainer", team_leader: "TL" };
const PILIHAN_PERAN = [
  ["Trainer", ["trainer"]],
  ["Team Leader", ["team_leader"]],
  ["Keduanya", ["trainer", "team_leader"]]
];
const samaPeran = (a, b) => a.length === b.length && a.every((r) => b.includes(r));

// Keterangan chip cabang bila perannya di cabang itu lebih sempit dari peran akunnya.
function ketPeranCabang(u, bid) {
  const akun = PERAN_CABANG.filter((r) => (u.roles || []).includes(r));
  const p = (u.effectiveBranchRoles || {})[bid];
  if (!akun.length || !Array.isArray(p) || p.length >= akun.length) return null;
  return p.length ? p.map((r) => SINGKAT[r]).join("+") : "—";
}

export default function UsersPage({ user, activeBranch, setToast }) {
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [modal, setModal] = useState(null);

  const isSuperAdmin = !!user?.isSuperAdmin;
  // Filter cabang halaman (karena user bisa 70+). Default: cabang aktif.
  // "" = Semua Cabang (hanya untuk super admin).
  const [branchFilter, setBranchFilter] = useState(
    activeBranch?.id != null ? String(activeBranch.id) : ""
  );

  const branchById = new Map(branches.map((b) => [b.id, b]));

  // Filter TAMPILAN: semua admin boleh melihat cabang mana pun (mis. untuk menambah
  // trainer yang mengajar di 2 cabang). Opsi = semua cabang aktif.
  const branchOptions = branches;

  async function load() {
    setLoading(true);

    try {
      const [res, br] = await Promise.all([
        api.getUsers(branchFilter),
        api.getBranches()
      ]);

      if (res.success) {
        setRows(res.data || []);
      }
      if (br?.success) {
        setBranches(br.data || []);
      }
    } catch (err) {
      setToast({
        type: "error",
        message: err.message || "Failed to load users"
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchFilter]);

  const visible = rows.filter((u) =>
    [u.code, u.name, u.nick, u.email, u.pos]
      .join(" ")
      .toLowerCase()
      .includes(query.toLowerCase())
  );

  async function save(payload) {
    try {
      const res = await api.saveUser(payload);

      setToast({
        type: res.success ? "success" : "error",
        message: res.message || "Saved"
      });

      if (res.success) {
        setModal(null);
        load();
      }
    } catch (err) {
      setToast({
        type: "error",
        message: err.message || "Save failed"
      });
    }
  }

  async function remove(user) {
    if (
      !window.confirm(
        `Delete account "${user.name || user.email}"? This cannot be undone.`
      )
    ) {
      return;
    }

    try {
      const res = await api.deleteUser(user.rowNumber);

      setToast({
        type: res.success ? "success" : "error",
        message: res.message || "Deleted"
      });

      if (res.success) load();
    } catch (err) {
      setToast({
        type: "error",
        message: err.message || "Delete failed"
      });
    }
  }

  return (
    <>
      <PageTitle
        title="User Accounts"
        subtitle="Generate and manage system access for all staff."
        action={
          <div className="flex flex-wrap gap-2">
            {/* Filter cabang — default cabang aktif; bisa lihat cabang lain / semua
                (untuk kasus trainer yang mengajar di 2 cabang). */}
            {branchOptions.length > 1 && (
              <Select
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                className="w-44"
              >
                <option value="">Semua Cabang</option>
                {branchOptions.map((b) => (
                  <option key={b.id} value={String(b.id)}>
                    {b.code}
                    {b.name ? ` · ${b.name}` : ""}
                  </option>
                ))}
              </Select>
            )}

            <Input
              placeholder="Search users..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />

            <Button onClick={() => setModal({})}>
              <Icon name="person_add" />
              Create User
            </Button>
          </div>
        }
      />

      {loading ? (
        <Spinner />
      ) : (
        <DataCard>
          <table className="w-full min-w-[720px] whitespace-nowrap text-left">
            <thead>
              <tr>
                {[
                  "CODE",
                  "NAME & NICKNAME",
                  "ROLE",
                  "CONTACT",
                  "ACTIONS"
                ].map((h) => (
                  <Th key={h} right={h === "ACTIONS"}>
                    {h}
                  </Th>
                ))}
              </tr>
            </thead>

            <tbody>
              {visible.map((u) => (
                <tr
                  key={u.rowNumber}
                  className="border-t border-slate-200 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-primary/5"
                >
                  <Td mono primary>
                    {u.code}
                  </Td>

                  <Td>
                    <p className="font-black">{u.name}</p>
                    <p className="text-xs text-slate-500">{u.nick}</p>
                  </Td>

                  <Td>
                    <div className="flex flex-wrap gap-1">
                      {(u.roles || []).length
                        ? (u.roles || []).map((r) => (
                            <Badge key={r}>{ROLE_LABEL[r] || r}</Badge>
                          ))
                        : <Badge>{u.pos}</Badge>}
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {u.isSuperAdmin ? (
                        <span className="rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-bold text-primary">
                          Semua Cabang
                        </span>
                      ) : (u.branchIds || []).length ? (
                        (u.branchIds || []).map((bid) => {
                          const ket = ketPeranCabang(u, bid);
                          return (
                            <span
                              key={bid}
                              title={ket === "—" ? "Bukan trainer maupun team leader di cabang ini" : undefined}
                              className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500 dark:bg-slate-800"
                            >
                              {branchById.get(bid)?.code || `#${bid}`}
                              {ket ? ` · ${ket}` : ""}
                            </span>
                          );
                        })
                      ) : (
                        <span className="text-[10px] text-slate-400">
                          Tanpa cabang
                        </span>
                      )}
                    </div>
                  </Td>

                  <Td>
                    <p>{u.email}</p>
                    <p className="text-xs text-slate-500">{u.wa}</p>
                  </Td>

                  <Td right>
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" onClick={() => setModal(u)}>
                        <Icon name="edit" />
                        Edit
                      </Button>
                      <Button variant="danger" onClick={() => remove(u)}>
                        <Icon name="delete" />
                        Delete
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))}

              {!visible.length && (
                <tr>
                  <Td colSpan="5">No users found.</Td>
                </tr>
              )}
            </tbody>
          </table>
        </DataCard>
      )}

      {/* Admin biasa hanya boleh menugaskan ke cabangnya sendiri → batasi checkbox cabang. */}
      <UserModal
        open={!!modal}
        user={modal || {}}
        branches={isSuperAdmin ? branches : (user?.branches || [])}
        canSetSuperAdmin={isSuperAdmin}
        onClose={() => setModal(null)}
        onSave={save}
      />
    </>
  );
}

function UserModal({ open, user, branches = [], canSetSuperAdmin = false, onClose, onSave }) {
  const [form, setForm] = useState(user);
  // Pilihan Trainer / Team Leader per cabang (id cabang → peran). Hanya dipakai untuk akun
  // yang memegang keduanya; cabang tanpa pilihan = Keduanya (ikut peran akun).
  const [peranCabang, setPeranCabang] = useState({});

  useEffect(() => {
    // Akun baru default role Trainer; edit → pakai roles yang ada.
    setForm({
      ...user,
      roles: Array.isArray(user.roles) ? user.roles : (user.rowNumber ? [] : ["trainer"])
    });
    // Pembagian per cabang yang tersimpan hanya berarti bila akunnya SUDAH Trainer + Team
    // Leader. Akun yang baru diberi peran kedua mulai dari "Keduanya" di semua cabangnya —
    // sama dengan akibatnya bila tak diatur per cabang.
    const akun = Array.isArray(user.roles) ? user.roles : [];
    const sudahKeduanya = PERAN_CABANG.every((r) => akun.includes(r));
    setPeranCabang(sudahKeduanya ? { ...(user.effectiveBranchRoles || {}) } : {});
  }, [user]);

  function patch(key, value) {
    setForm({
      ...form,
      [key]: value
    });
  }

  const selectedBranches = Array.isArray(form.branchIds) ? form.branchIds : [];
  const selectedRoles = Array.isArray(form.roles) ? form.roles : [];
  const isSuper = !!form.isSuperAdmin;

  // Role yang boleh diberikan (Management eksklusif Super Admin). Admin biasa boleh
  // Trainer/Team Leader/Admin cabang (dibatasi cabangnya di backend).
  const roleOptions = ROLE_OPTIONS.filter((r) => r.key !== "management" || canSetSuperAdmin);

  function toggleRole(key) {
    const next = selectedRoles.includes(key)
      ? selectedRoles.filter((x) => x !== key)
      : [...selectedRoles, key];
    patch("roles", next);
  }

  function toggleBranch(id) {
    const next = selectedBranches.includes(id)
      ? selectedBranches.filter((x) => x !== id)
      : [...selectedBranches, id];
    patch("branchIds", next);
  }

  // Peran per cabang hanya diatur bila akun ini Trainer sekaligus Team Leader.
  const aturPerCabang = !isSuper && PERAN_CABANG.every((r) => selectedRoles.includes(r));
  const cabangDicentang = branches.filter((b) => selectedBranches.includes(b.id));
  const pilihanCabang = (id) => (Array.isArray(peranCabang[id]) ? peranCabang[id] : PERAN_CABANG);

  function simpan() {
    // effectiveBranchRoles hanya untuk tampilan; yang dikirim adalah pilihan di formulir ini.
    const { effectiveBranchRoles, ...data } = form;
    onSave(aturPerCabang
      ? { ...data, branchRoles: Object.fromEntries(cabangDicentang.map((b) => [b.id, pilihanCabang(b.id)])) }
      : data);
  }

  return (
    <Modal
      title={form?.rowNumber ? "Edit User" : "Create User"}
      open={open}
      onClose={onClose}
    >
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Employee Code">
          <Input
            value={form.code || ""}
            onChange={(e) => patch("code", e.target.value)}
          />
        </Field>

        <Field label="Role akun (boleh lebih dari satu)">
          <div className="flex flex-wrap gap-2">
            {roleOptions.map((r) => {
              const on = selectedRoles.includes(r.key);
              return (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => toggleRole(r.key)}
                  className={cx(
                    "rounded-lg border px-3 py-1.5 text-xs font-bold transition",
                    on
                      ? "border-primary bg-primary text-white"
                      : "border-slate-200 text-slate-500 hover:border-primary/40 dark:border-slate-700"
                  )}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
        </Field>

        <Field label="Bracket Gaji">
          <div className="rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-500 dark:border-slate-700">
            Diatur <b>per cabang</b> di <b>Trainer Payroll</b> (Final Salary) — bukan di
            sini. Bracket boleh berbeda tiap cabang.
          </div>
        </Field>

        <Field label="Full Name">
          <Input
            value={form.name || ""}
            onChange={(e) => patch("name", e.target.value)}
          />
        </Field>

        <Field label="Nickname">
          <Input
            value={form.nick || ""}
            onChange={(e) => patch("nick", e.target.value)}
          />
        </Field>

        <Field label="WhatsApp">
          <Input
            value={form.wa || ""}
            onChange={(e) => patch("wa", e.target.value)}
          />
        </Field>

        <Field label="Email">
          <Input
            type="email"
            value={form.email || ""}
            onChange={(e) => patch("email", e.target.value)}
          />
        </Field>

        <Field label={form?.rowNumber ? "Password Baru (opsional)" : "Password"}>
          <Input
            type="password"
            value={form.pass || ""}
            placeholder={form?.rowNumber ? "Kosongkan jika tidak diubah" : "Masukkan password"}
            onChange={(e) => patch("pass", e.target.value)}
          />
        </Field>
      </div>

      {/* ── Penugasan cabang (multi-cabang) ── */}
      <div className="mt-5 rounded-xl border border-slate-200 p-4 dark:border-primary/15">
        <div className="flex items-center justify-between">
          <p className="text-sm font-black">Cabang</p>
          {/* Hanya SUPER ADMIN yang bisa memberi/mencabut status super admin. */}
          {canSetSuperAdmin ? (
            <label className="flex cursor-pointer items-center gap-2 text-xs font-bold text-slate-500">
              <input
                type="checkbox"
                checked={isSuper}
                onChange={(e) => patch("isSuperAdmin", e.target.checked)}
                className="rounded text-primary focus:ring-primary"
              />
              Super Admin (semua cabang)
            </label>
          ) : isSuper ? (
            <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold uppercase text-primary">
              Super Admin
            </span>
          ) : null}
        </div>

        {isSuper ? (
          <p className="mt-3 rounded-lg bg-primary/10 px-3 py-2 text-xs font-bold text-primary">
            Super admin dapat membuka & melihat SEMUA cabang.
          </p>
        ) : branches.length === 0 ? (
          <p className="mt-3 text-xs text-slate-400">
            Belum ada cabang. Buat cabang dulu (lihat template SQL) sebelum menugaskan.
          </p>
        ) : (
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {branches.map((b) => (
              <label
                key={b.id}
                className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-2 text-xs font-bold hover:border-primary/40 dark:border-slate-700"
              >
                <input
                  type="checkbox"
                  checked={selectedBranches.includes(b.id)}
                  onChange={() => toggleBranch(b.id)}
                  className="rounded text-primary focus:ring-primary"
                />
                <span className="truncate" title={b.name || b.code}>
                  {b.code}
                  {b.name ? ` · ${b.name}` : ""}
                </span>
              </label>
            ))}
          </div>
        )}

        {aturPerCabang && cabangDicentang.length > 0 && (
          <div className="mt-4 border-t border-slate-200 pt-4 dark:border-primary/15">
            <p className="text-sm font-black">Peran di tiap cabang</p>
            <p className="mt-1 text-xs text-slate-500">
              Akun ini Trainer sekaligus Team Leader. Hak team leader (payroll, absensi, jadwal, raport) hanya
              berlaku di cabang tempat ia Team Leader — mis. Trainer di BR01, Team Leader di BR07.
            </p>
            <div className="mt-3 space-y-2">
              {cabangDicentang.map((b) => {
                const p = pilihanCabang(b.id);
                return (
                  <div key={b.id} className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-bold">
                      {b.code}
                      {b.name ? ` · ${b.name}` : ""}
                      {!p.length && (
                        <span className="ml-2 font-normal text-amber-600 dark:text-amber-400">
                          saat ini bukan trainer maupun team leader
                        </span>
                      )}
                    </span>
                    <div className="flex gap-1" role="radiogroup" aria-label={`Peran di ${b.code}`}>
                      {PILIHAN_PERAN.map(([label, nilai]) => {
                        const on = samaPeran(p, nilai);
                        return (
                          <button
                            key={label}
                            type="button"
                            role="radio"
                            aria-checked={on}
                            onClick={() => setPeranCabang({ ...peranCabang, [b.id]: nilai })}
                            className={cx(
                              "rounded-lg border px-2.5 py-1 text-[11px] font-bold transition",
                              on
                                ? "border-primary bg-primary text-white"
                                : "border-slate-200 text-slate-500 hover:border-primary/40 dark:border-slate-700"
                            )}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <Button className="mt-5 w-full" onClick={simpan}>
        Save User Record
      </Button>
    </Modal>
  );
}